import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "grid grid-cols-1 gap-4 lg:grid-cols-2" }
const _hoisted_4 = { class: "flex-none items-end lg:flex" }
const _hoisted_5 = { class: "mt-6 w-full" }
const _hoisted_6 = { class: "flex justify-end px-4 sm:px-0" }

import { computed, reactive, Ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { required, maxLength } from '@/utils/validators';

import { useUiStore } from '@/stores';
import { goBack } from '@/composables/useNavigation';
import { IntegrationService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    FormAction,
    DefaultServiceResult,
    IntegrationServiceCreateResult,
    IntegrationType,
    PipedriveIntegrationModel,
    PipedriveIntegrationFormState,
    Permission,
} from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'PipedriveIntegrationFormPage',
  props: {
    action: {
        type: String,
        default: FormAction.CREATE,
    },
},
  setup(__props) {

const uiStore = useUiStore();
const route = useRoute();

const props = __props;

const formState = reactive<PipedriveIntegrationFormState>({
    type: IntegrationType.Pipedrive,
    token: null,
    checkActivityAsDone: true,
    associateWithDeal: true,
    isActive: true,
});

const rules = computed<ValidationArgs<PipedriveIntegrationFormState>>(() => ({
    type: { required },
    token: { required, maxLength: maxLength(250) },
    checkActivityAsDone: { required },
    associateWithDeal: { required },
    isActive: { required },
}));

const v$: Ref<Validation<ValidationArgs<PipedriveIntegrationFormState>>> = useVuelidate(
    rules,
    formState,
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getIntegration = async (id: number) => {
    await setLoading(true);
    try {
        const { data } = await IntegrationService.get<PipedriveIntegrationModel>(id);
        Object.assign(formState, data);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const onSubmit = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    await setLoading(true);

    try {
        let response: AxiosResponse<DefaultServiceResult | IntegrationServiceCreateResult>;

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            response = await IntegrationService.update<DefaultServiceResult>(id, formState);
        } else {
            response = await IntegrationService.create<IntegrationServiceCreateResult>(formState);
        }

        toast.success(response.data.message);
        v$.value.$reset();
        goBack({ name: 'ListIntegration' });
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

onMounted(() => {
    if (props.action === FormAction.EDIT) {
        getIntegration(Number(route.params.id));
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('core.entity.Integrations')) + " - " + _toDisplayString(_ctx.$t('integration.integrations.pipedrive.title')), 1)
      ]),
      _: 1
    }),
    _createElementVNode("form", {
      class: "space-y-4",
      autocomplete: "off",
      onSubmit: _withModifiers(onSubmit, ["prevent"])
    }, [
      _createVNode(_component_UiPanel, null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_UiTextInput, {
              modelValue: _unref(v$).token.$model,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).token.$model) = $event)),
              name: "token",
              type: "text",
              label: _ctx.$t('integration.integrations.pipedrive.form.token'),
              errors: _unref(v$).token.$errors,
              onBlur: _unref(v$).token.$touch
            }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_UiCheckboxInput, {
                modelValue: _unref(v$).checkActivityAsDone.$model,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).checkActivityAsDone.$model) = $event)),
                name: "checkActivityAsDone",
                label: 
                                _ctx.$t('integration.integrations.pipedrive.form.checkActivityAsDone')
                            ,
                class: "mb-3 ml-0 mr-0 lg:ml-4 lg:mr-4"
              }, null, 8, ["modelValue", "label"]),
              _createVNode(_component_UiCheckboxInput, {
                modelValue: _unref(v$).associateWithDeal.$model,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).associateWithDeal.$model) = $event)),
                name: "associateWithDeal",
                label: _ctx.$t('integration.integrations.pipedrive.form.associateWithDeal'),
                class: "mb-3 ml-0 lg:ml-4"
              }, null, 8, ["modelValue", "label"])
            ])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_UiCheckboxInput, {
              modelValue: _unref(v$).isActive.$model,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).isActive.$model) = $event)),
              name: "isActive",
              label: _ctx.$t('integration.integrations.pipedrive.form.isActive')
            }, null, 8, ["modelValue", "label"])
          ])
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_6, [
        _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListIntegration' } }),
        (_ctx.$can(_unref(Permission).EDIT_INTEGRATION))
          ? (_openBlock(), _createBlock(_component_UiButton, {
              key: 0,
              type: "submit",
              variant: "primary",
              "text-variant": "white",
              class: "ml-4"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ])
    ], 32),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ]))
}
}

})